import { GripVertical } from 'lucide-react'
import { forwardRef } from 'react'
import type { ButtonHTMLAttributes } from 'react'

import { cn } from '@/shared/lib/cn'
import { useT } from '@/shared/i18n'

export interface DragHandleProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Qué se arrastra. Va al `aria-label`: "Reordenar Portal de proveedores". */
  label: string
  disabled?: boolean
  /** Por qué no se puede arrastrar. Se muestra como tooltip nativo. */
  disabledReason?: string
  isDragging?: boolean
}

/**
 * El handle de arrastre.
 *
 * **Siempre visible, nunca en hover** (plan 1): el hover-only cuesta tiempo
 * todos los días y es invisible con teclado. Es un `<button>` de verdad, así que
 * entra en el orden de tabulación y el sensor de teclado se engancha ahí.
 */
export const DragHandle = forwardRef<HTMLButtonElement, DragHandleProps>(function DragHandle(
  { label, disabled = false, disabledReason, isDragging = false, className, ...props },
  ref,
) {
  const t = useT()

  return (
    <button
      ref={ref}
      type="button"
      aria-label={t(disabled ? 'dnd.cannotReorder' : 'dnd.reorder', { item: label })}
      aria-disabled={disabled || undefined}
      disabled={disabled}
      title={disabled ? disabledReason : t('dnd.keyboardHint')}
      className={cn(
        'flex h-5 w-4 shrink-0 items-center justify-center rounded-[var(--radius-control)]',
        'transition-colors duration-[var(--motion-fast)]',
        disabled
          ? 'cursor-not-allowed text-text-3/40'
          : 'cursor-grab text-text-3 hover:bg-surface-2 hover:text-text-1',
        isDragging && 'cursor-grabbing text-signal',
        className,
      )}
      {...props}
    >
      <GripVertical size={13} aria-hidden="true" />
    </button>
  )
})
